/**
 * The measurement/interaction TARGETS: what a handle in the scene represents, and
 * the factories that build them.
 *
 * A leaf module on purpose. These are plain data constructors, not part of the React
 * controller that consumes them — and putting them here is what breaks a genuine
 * import cycle: `interiorHandles` needs the factories, `ViewerMeasurementController`
 * needs `InteriorDragController`, and `InteriorDragController` needs
 * `interiorHandles`. `ViewerMeasurementController` re-exports everything here, so
 * existing importers are unaffected.
 */
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type {
	BenchRun,
	BenchRunEdge,
	HeaterPlacement,
	HeaterSizeMm,
} from "../../domain/interior";
import type { BuildInput } from "../../engines/types";
import type { InteriorDragTarget } from "./interiorDragTarget";

export type MeasurementWall = WallOpening["wall"];

const WALL_HOVER_OPACITY = 0.07;
const WALL_LOCKED_OPACITY = 0.12;
/** Shared with `ViewerMeasurementController`'s own hover/locked fallbacks. */
export const INTERACTION_HOVER_OPACITY = 0.2;
export const INTERACTION_LOCKED_OPACITY = 0.28;

/**
 * Which layer of the model a handle belongs to.
 *
 * `shell` is the building envelope — walls, the openings in them, the partition.
 * `interior` is what stands inside it. The picker resolves by DISTANCE (2026-04-29
 * note), so when the interior is being edited every shell handle has to leave the
 * candidate set: they all sit between an outside camera and the room. Excluding by
 * LAYER rather than by kind is what makes that complete — excluding only
 * `kind === "wall"` left the opening handles behind, and a front-window handle sits
 * at `z = L/2 - 0.09`, four millimetres NEARER the camera than the wall plane at
 * `L/2 - 0.094` it replaced. Aiming at a bench behind a window started an opening
 * drag.
 */
export type MeasurementPickLayer = "shell" | "interior";

interface BaseMeasurementTarget {
	id: string;
	draggable?: boolean;
	priority: number;
	/** Defaults to `shell` — every pre-existing handle is envelope geometry. */
	pickLayer?: MeasurementPickLayer;
}

export interface WallMeasurementTarget extends BaseMeasurementTarget {
	kind: "wall";
	wall: MeasurementWall;
	wallLengthMm: number;
}

export interface OpeningMeasurementTarget extends BaseMeasurementTarget {
	kind: "opening";
	openingId: string;
	openingType: WallOpening["type"];
	wall: MeasurementWall;
	widthMm: number;
	heightMm: number;
	xMm: number;
	yMm: number;
	offsetStartMm: number;
	offsetEndMm: number;
}

export interface PartitionMeasurementTarget extends BaseMeasurementTarget {
	kind: "partition";
	saunaDepthMm: number;
	loungeDepthMm: number;
	partitionSplit: number;
}

/**
 * A lav's body — the handle that slides the whole run along its wall.
 *
 * `runId` is the layout's own stable id, so a selection survives an edit and the
 * drag can find the run it was made against (`BenchRun.id`).
 */
export interface BenchMeasurementTarget extends BaseMeasurementTarget {
	kind: "bench";
	/** Present on the vertical handle and its drag readout. */
	seatHeightMm?: number;
	runId: string;
	tier: BenchRun["tier"];
	wall: BenchRun["wall"];
	startMm: number;
	lengthMm: number;
	depthMm: number;
	/**
	 * What a drag on this handle moves. Carried on the target so the id format and
	 * the kind→target mapping have ONE owner: the handle factory. Decoding it back
	 * out of `kind` + `runId` at the drag site meant three places had to agree.
	 */
	planTarget: InteriorDragTarget;
}

/** One END of a lav — the handle that changes its length. */
export interface BenchEdgeMeasurementTarget extends BaseMeasurementTarget {
	kind: "bench-edge";
	runId: string;
	edge: BenchRunEdge;
	tier: BenchRun["tier"];
	wall: BenchRun["wall"];
	startMm: number;
	lengthMm: number;
	depthMm: number;
	planTarget: InteriorDragTarget;
}

export interface HeaterMeasurementTarget extends BaseMeasurementTarget {
	kind: "heater";
	wall: HeaterPlacement["wall"];
	offsetMm: number;
	wallGapMm: number;
	widthMm: number;
	depthMm: number;
	planTarget: InteriorDragTarget;
}

export type MeasurementTarget =
	| WallMeasurementTarget
	| OpeningMeasurementTarget
	| PartitionMeasurementTarget
	| BenchMeasurementTarget
	| BenchEdgeMeasurementTarget
	| HeaterMeasurementTarget;

export type MeasurementTargetKind = MeasurementTarget["kind"];

export function createWallMeasurementTarget(
	wall: MeasurementWall,
	wallLengthMm: number,
): WallMeasurementTarget {
	return {
		id: `wall:${wall}`,
		kind: "wall",
		wall,
		wallLengthMm,
		priority: 1,
	};
}

export function createOpeningMeasurementTarget(
	opening: WallOpening,
	wallLengthMm: number,
): OpeningMeasurementTarget {
	return {
		id: `opening:${opening.id}`,
		kind: "opening",
		openingId: opening.id,
		openingType: opening.type,
		wall: opening.wall,
		widthMm: opening.widthMm,
		heightMm: opening.heightMm,
		xMm: opening.xMm,
		yMm: opening.yMm,
		offsetStartMm: opening.xMm,
		offsetEndMm: Math.max(0, wallLengthMm - opening.xMm - opening.widthMm),
		draggable: true,
		priority: 3,
	};
}

export function createPartitionMeasurementTarget(
	buildInput: BuildInput,
): PartitionMeasurementTarget {
	const saunaDepthMm = Math.round(
		(buildInput.partitionSplit ?? 0.5) * buildInput.dimensions.length * 1000,
	);
	const totalLengthMm = Math.round(buildInput.dimensions.length * 1000);
	return {
		id: "partition",
		kind: "partition",
		saunaDepthMm,
		loungeDepthMm: Math.max(0, totalLengthMm - saunaDepthMm),
		partitionSplit: buildInput.partitionSplit ?? 0.5,
		draggable: true,
		priority: 2,
	};
}

/**
 * The three interior targets — the ONE owner of their ids, their pick layer and
 * what a drag on each of them moves.
 *
 * `priority` is inherited from the sibling factories for shape only; the picker
 * resolves by distance (2026-04-29 note) and nothing reads the field. Every one of
 * them is `draggable`, which is what `maybeStartDrag` gates on and what turns the
 * cursor into a grab hand.
 */
export function createBenchMeasurementTarget(
	run: BenchRun,
): BenchMeasurementTarget {
	return {
		id: `bench:${run.id}`,
		kind: "bench",
		runId: run.id,
		tier: run.tier,
		wall: run.wall,
		startMm: run.startMm,
		lengthMm: run.lengthMm,
		depthMm: run.depthMm,
		planTarget: { kind: "bench", runId: run.id },
		draggable: true,
		pickLayer: "interior",
		priority: 3,
	};
}

export function createBenchEdgeMeasurementTarget(
	run: BenchRun,
	edge: BenchRunEdge,
): BenchEdgeMeasurementTarget {
	return {
		id: `bench-edge:${run.id}:${edge}`,
		kind: "bench-edge",
		runId: run.id,
		edge,
		// The run's own measurements, so the card shows the same three numbers
		// whether the body or an end is hovered — a readout that changed shape
		// between the two read as a bug.
		tier: run.tier,
		wall: run.wall,
		startMm: run.startMm,
		lengthMm: run.lengthMm,
		depthMm: run.depthMm,
		planTarget: { kind: "bench-edge", runId: run.id, edge },
		draggable: true,
		pickLayer: "interior",
		priority: 4,
	};
}

export function createHeaterMeasurementTarget(
	placement: HeaterPlacement,
	size: HeaterSizeMm,
): HeaterMeasurementTarget {
	return {
		id: "heater",
		kind: "heater",
		wall: placement.wall,
		offsetMm: placement.offsetMm,
		wallGapMm: placement.wallGapMm,
		widthMm: size.widthMm,
		depthMm: size.depthMm,
		planTarget: { kind: "heater" },
		draggable: true,
		pickLayer: "interior",
		priority: 3,
	};
}

export function getWallLengthMm(
	wall: MeasurementWall,
	buildInput: BuildInput,
): number {
	if (wall === "divider") {
		return Math.max(0, Math.round(buildInput.dimensions.width * 1000) - 190);
	}
	if (wall === "front" || wall === "back") {
		return Math.round(buildInput.dimensions.width * 1000);
	}
	return Math.round(buildInput.dimensions.length * 1000);
}

export function getWallOverlayOpacities(kind: MeasurementTarget["kind"]) {
	if (kind === "wall") {
		return {
			baseOpacity: 0,
			baseOpacityHover: WALL_HOVER_OPACITY,
			baseOpacityLocked: WALL_LOCKED_OPACITY,
		};
	}

	// Openings, the lav's body and the aggregat recede completely at rest.
	// Opening pills already identify the drag target; a tinted box over clear
	// glass makes it look frosted. The bench END handles stay faintly on, because a
	// touch surface has no hover with which to reveal one (the plan view made the
	// same call).
	if (kind === "opening" || kind === "bench" || kind === "heater") {
		return {
			baseOpacity: 0,
			baseOpacityHover: INTERACTION_HOVER_OPACITY,
			baseOpacityLocked: INTERACTION_LOCKED_OPACITY,
		};
	}
	if (kind === "bench-edge") {
		return {
			baseOpacity: 0.5,
			baseOpacityHover: 0.85,
			baseOpacityLocked: 0.95,
		};
	}

	return {
		baseOpacity: 0.08,
		baseOpacityHover: INTERACTION_HOVER_OPACITY,
		baseOpacityLocked: INTERACTION_LOCKED_OPACITY,
	};
}
